import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Btn, Empty } from "../components/UI";
import { SlidePanel, ConfirmDialog, ReadOnlyChip } from "../components/SlidePanel";
import { FLabel, FInput, FTextarea, FSelect, CheckBox } from "./engagement/FormControls";
import { useAuth } from "../context/AuthContext";
import { Plus, Pencil, Archive, FolderKanban, Users, MapPin, CalendarClock, Trash2, Wrench, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const PROGRAM_TYPES = ["Outpatient", "Intensive Outpatient (IOP)", "Partial Hospitalization (PHP)", "Residential",
  "Psychosocial Rehabilitation (PSR)", "ACT (Assertive Community Treatment)", "Day Program", "Case Management",
  "Crisis Stabilization", "MAT (Medication-Assisted Treatment)", "Peer Support", "Other"];
const ASAM_LEVELS = ["N/A", "0.5 Early Intervention", "1.0 Outpatient", "2.1 IOP", "2.5 PHP", "3.1 Low-Intensity Residential",
  "3.3 Clinically Managed Residential", "3.5 High-Intensity Residential", "3.7 Medically Monitored", "4.0 Medically Managed"];
const POPULATIONS = ["Adults (18+)", "Adolescents (13-17)", "Children (0-12)", "Transition Age (16-25)", "Older Adults (60+)", "All ages"];
const SPECIALTIES = ["Mental Health", "Substance Use", "Co-occurring (MH + SUD)", "Developmental Disabilities", "Other"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const CF_TYPES = [["text", "Text"], ["date", "Date"], ["number", "Number"], ["select", "Dropdown"]];

const EMPTY = { name: "", code: "", description: "", program_type: "Outpatient", asam_level: "N/A", population: "Adults (18+)",
  specialty: "Mental Health", capacity: "", location: "", room: "", operating_days: ["Mon", "Tue", "Wed", "Thu", "Fri"],
  start_time: "09:00", end_time: "17:00", staff_ratio: "", director: "", medical_director: "",
  start_date: "", end_date: "", license_number: "", license_renewal: "", accrediting_body: "CARF",
  accreditation_expiry: "", funding_sources: "", default_cpt: "", payers_accepted: "", unit_definition: "",
  expected_los: "", auth_required: false, admission_criteria: "", discharge_criteria: "",
  assessment_frequency: "", plan_review_days: "90", custom_fields: [] };

function Section({ title, children }) {
  return (
    <div className="rounded-xl border border-[#EFEBE0] p-4">
      <div className="mb-3 text-xs font-bold uppercase tracking-wide text-[#7B6157]">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function CustomFieldsBuilder({ fields, onChange }) {
  const set = (i, k, v) => onChange(fields.map((f, ix) => (ix === i ? { ...f, [k]: v } : f)));
  return (
    <div className="space-y-2" data-testid="program-custom-fields">
      {fields.map((f, i) => (
        <div key={i} className="rounded-xl bg-[#FDFCF8] border border-[#F1EEE7] p-3">
          <div className="grid grid-cols-2 gap-2">
            <FInput data-testid={`program-cf-label-${i}`} placeholder="Field label (e.g., Grant contract #)" value={f.label || ""} onChange={(e) => set(i, "label", e.target.value)} />
            <div className="flex gap-2">
              <FSelect data-testid={`program-cf-type-${i}`} value={f.type || "text"} onChange={(e) => set(i, "type", e.target.value)}>
                {CF_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </FSelect>
              <button type="button" data-testid={`program-cf-remove-${i}`} onClick={() => onChange(fields.filter((_, ix) => ix !== i))}
                className="shrink-0 rounded-lg p-2 text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={14} /></button>
            </div>
          </div>
          <div className="mt-2">
            {f.type === "select" ? (
              <div className="grid grid-cols-2 gap-2">
                <FInput data-testid={`program-cf-options-${i}`} placeholder="Options, comma-separated" value={f.options || ""} onChange={(e) => set(i, "options", e.target.value)} />
                <FSelect data-testid={`program-cf-value-${i}`} value={f.value || ""} onChange={(e) => set(i, "value", e.target.value)}>
                  {(f.options || "").split(",").map((o) => o.trim()).filter(Boolean).map((o) => <option key={o}>{o}</option>)}
                </FSelect>
              </div>
            ) : (
              <FInput data-testid={`program-cf-value-${i}`} type={f.type === "date" ? "date" : f.type === "number" ? "number" : "text"}
                placeholder="Value" value={f.value || ""} onChange={(e) => set(i, "value", e.target.value)} />
            )}
          </div>
        </div>
      ))}
      <button type="button" data-testid="program-cf-add" onClick={() => onChange([...fields, { label: "", type: "text", value: "" }])}
        className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-[#D7CCC8] py-2 text-xs font-semibold text-[#7B6157] hover:bg-[#FDFCF8]">
        <Plus size={12} /> Add custom field
      </button>
    </div>
  );
}

function ProgramCard({ p, isAdmin, onEdit, onArchive }) {
  const cap = Number(p.capacity) || 0;
  const census = p.census || 0;
  const pctFull = cap ? Math.min(100, Math.round((census / cap) * 100)) : null;
  const soon = (d) => d && dayjs(d).diff(dayjs(), "day") < 60;
  const dates = [["License renewal", p.license_renewal], [`${p.accrediting_body || "Accreditation"} expiry`, p.accreditation_expiry]].filter(([, d]) => d);
  return (
    <Card className="p-5 flex flex-col" data-testid={`program-card-${p.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 font-heading font-bold text-[#3E2723]">
            <FolderKanban size={16} className="text-[#7B6157]" /> {p.name}
            {p.code && <span className="rounded-md bg-[#F5F2EB] px-1.5 py-0.5 text-[10px] font-mono text-[#7B6157]">{p.code}</span>}
          </div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {p.program_type && <span className="rounded-full bg-[#EDE7DC] px-2 py-0.5 text-[10px] font-semibold text-[#5D4037]">{p.program_type}</span>}
            {p.asam_level && p.asam_level !== "N/A" && <span className="rounded-full bg-[#E3F2FD] px-2 py-0.5 text-[10px] font-semibold text-[#1565C0]">ASAM {p.asam_level.split(" ")[0]}</span>}
            {p.population && <span className="rounded-full bg-[#F1F8E9] px-2 py-0.5 text-[10px] font-semibold text-[#33691E]">{p.population}</span>}
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-1.5">
            <button type="button" data-testid={`edit-program-${p.id}`} aria-label="Edit" onClick={onEdit}
              className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Pencil size={13} /></button>
            <button type="button" data-testid={`archive-program-${p.id}`} aria-label="Archive" onClick={onArchive}
              className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828]"><Archive size={13} /></button>
          </div>
        )}
      </div>
      {p.description && <p className="mt-2 text-sm text-[#6D4C41] line-clamp-2">{p.description}</p>}
      {pctFull !== null && (
        <div className="mt-3" data-testid={`program-census-${p.id}`}>
          <div className="flex justify-between text-[11px] text-[#7B6157]"><span className="flex items-center gap-1"><Users size={11} /> Census</span><span className="font-semibold">{census}/{cap} ({pctFull}%)</span></div>
          <div className="mt-1 h-1.5 rounded-full bg-[#F5F2EB]"><div className={`h-1.5 rounded-full ${pctFull >= 95 ? "bg-[#C62828]" : pctFull >= 80 ? "bg-[#E6A700]" : "bg-[#2E7D32]"}`} style={{ width: `${pctFull}%` }} /></div>
        </div>
      )}
      <div className="mt-3 space-y-1 text-xs text-[#7B6157]">
        {(p.location || p.room) && <div className="flex items-center gap-1.5"><MapPin size={11} /> {[p.location, p.room].filter(Boolean).join(" · ")}</div>}
        {(p.operating_days || []).length > 0 && <div className="flex items-center gap-1.5"><CalendarClock size={11} /> {p.operating_days.join(", ")} {p.start_time && `· ${p.start_time} to ${p.end_time}`}</div>}
        {p.director && <div>Director: <span className="font-medium text-[#5D4037]">{p.director}</span></div>}
        {dates.map(([l, d]) => (
          <div key={l} className={`flex items-center gap-1 ${soon(d) ? "text-[#C62828] font-semibold" : ""}`}>
            {soon(d) && <AlertTriangle size={11} />}{l}: {dayjs(d).format("MM/DD/YYYY")}
          </div>
        ))}
        {(p.custom_fields || []).filter((f) => f.label && f.value).map((f, i) => (
          <div key={i} className="flex items-center gap-1.5"><Wrench size={10} /> {f.label}: <span className="font-medium text-[#5D4037]">{f.type === "date" && f.value ? dayjs(f.value).format("MM/DD/YYYY") : f.value}</span></div>
        ))}
      </div>
    </Card>
  );
}

export default function ProgramsConfig() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [programs, setPrograms] = useState([]);
  const [panel, setPanel] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = () => api.get("/programs").then((r) => setPrograms(r.data));
  useEffect(() => { load(); }, []);
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) await api.put(`/programs/${editId}`, form);
      else await api.post("/programs", form);
      toast.success(editId ? "Program updated" : "Program added");
      setPanel(false); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const archive = async () => {
    setBusy(true);
    try {
      await api.delete(`/programs/${confirm.id}`);
      toast.success(`"${confirm.name}" archived`);
      setConfirm(null); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const openEdit = (p) => { setForm({ ...EMPTY, ...p, custom_fields: p.custom_fields || [], operating_days: p.operating_days || [] }); setEditId(p.id); setPanel(true); };

  return (
    <div data-testid="programs-config">
      <PageHeader title="Programs" subtitle="Enterprise program setup - capacity, schedules, licensing, billing defaults, criteria & custom fields"
        action={isAdmin
          ? <Btn data-testid="add-program-btn" onClick={() => { setForm(EMPTY); setEditId(null); setPanel(true); }}><Plus size={16} /> Add Program</Btn>
          : <ReadOnlyChip />} />
      {programs.length === 0 ? <Empty text="No programs configured yet" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {programs.map((p) => <ProgramCard key={p.id} p={p} isAdmin={isAdmin} onEdit={() => openEdit(p)} onArchive={() => setConfirm(p)} />)}
        </div>
      )}

      <SlidePanel open={panel} onClose={() => setPanel(false)} title={editId ? "Edit Program" : "Add Program"}
        subtitle="Programs drive intake, scheduling, billing defaults & compliance timers" width="max-w-2xl" testId="program-slide-panel">
        <form onSubmit={save} className="space-y-4 pb-10">
          <Section title="Identity">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><FLabel>Program Name *</FLabel><FInput data-testid="program-name-input" value={form.name} onChange={f("name")} required /></div>
              <div><FLabel>Code</FLabel><FInput data-testid="program-code-input" value={form.code} onChange={f("code")} placeholder="IOP-A" /></div>
            </div>
            <div><FLabel>Description</FLabel><FTextarea data-testid="program-desc-input" rows={2} value={form.description} onChange={f("description")} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><FLabel>Program type</FLabel><FSelect data-testid="program-type-select" value={form.program_type} onChange={f("program_type")}>{PROGRAM_TYPES.map((t) => <option key={t}>{t}</option>)}</FSelect></div>
              <div><FLabel>ASAM level of care</FLabel><FSelect data-testid="program-asam-select" value={form.asam_level} onChange={f("asam_level")}>{ASAM_LEVELS.map((t) => <option key={t}>{t}</option>)}</FSelect></div>
              <div><FLabel>Population served</FLabel><FSelect data-testid="program-population-select" value={form.population} onChange={f("population")}>{POPULATIONS.map((t) => <option key={t}>{t}</option>)}</FSelect></div>
              <div><FLabel>Specialty</FLabel><FSelect data-testid="program-specialty-select" value={form.specialty} onChange={f("specialty")}>{SPECIALTIES.map((t) => <option key={t}>{t}</option>)}</FSelect></div>
            </div>
          </Section>

          <Section title="Operations & Staffing">
            <div className="grid grid-cols-3 gap-3">
              <div><FLabel>Capacity (max census)</FLabel><FInput data-testid="program-capacity-input" type="number" value={form.capacity} onChange={f("capacity")} /></div>
              <div><FLabel>Location / site</FLabel><FInput data-testid="program-location-input" value={form.location} onChange={f("location")} /></div>
              <div><FLabel>Room</FLabel><FInput data-testid="program-room-input" value={form.room} onChange={f("room")} /></div>
              <div><FLabel>Staff : client ratio</FLabel><FInput data-testid="program-ratio-input" value={form.staff_ratio} onChange={f("staff_ratio")} placeholder="1:8" /></div>
              <div><FLabel>Program director</FLabel><FInput data-testid="program-director-input" value={form.director} onChange={f("director")} /></div>
              <div><FLabel>Medical director</FLabel><FInput data-testid="program-meddir-input" value={form.medical_director} onChange={f("medical_director")} /></div>
            </div>
            <div>
              <FLabel>Operating days</FLabel>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {DAYS.map((d) => (
                  <button key={d} type="button" data-testid={`program-day-${d.toLowerCase()}`}
                    onClick={() => setForm((s) => ({ ...s, operating_days: s.operating_days.includes(d) ? s.operating_days.filter((x) => x !== d) : [...s.operating_days, d] }))}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${form.operating_days.includes(d) ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>{d}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><FLabel>Opens</FLabel><FInput data-testid="program-start-time" type="time" value={form.start_time} onChange={f("start_time")} /></div>
              <div><FLabel>Closes</FLabel><FInput data-testid="program-end-time" type="time" value={form.end_time} onChange={f("end_time")} /></div>
            </div>
          </Section>

          <Section title="Key Dates & Licensing">
            <div className="grid grid-cols-2 gap-3">
              <div><FLabel>Program start date</FLabel><FInput data-testid="program-start-date" type="date" value={form.start_date} onChange={f("start_date")} /></div>
              <div><FLabel>Program end date (sunset)</FLabel><FInput data-testid="program-end-date" type="date" value={form.end_date} onChange={f("end_date")} /></div>
              <div><FLabel>License #</FLabel><FInput data-testid="program-license-input" value={form.license_number} onChange={f("license_number")} /></div>
              <div><FLabel>License renewal date</FLabel><FInput data-testid="program-license-renewal" type="date" value={form.license_renewal} onChange={f("license_renewal")} /></div>
              <div><FLabel>Accrediting body</FLabel><FSelect data-testid="program-accreditor" value={form.accrediting_body} onChange={f("accrediting_body")}>{["CARF", "Joint Commission", "COA", "None"].map((t) => <option key={t}>{t}</option>)}</FSelect></div>
              <div><FLabel>Accreditation expiry</FLabel><FInput data-testid="program-accreditation-expiry" type="date" value={form.accreditation_expiry} onChange={f("accreditation_expiry")} /></div>
            </div>
            <div><FLabel>Funding sources</FLabel><FInput data-testid="program-funding-input" value={form.funding_sources} onChange={f("funding_sources")} placeholder="GA DBHDD, SAMHSA block grant, Medicaid…" /></div>
          </Section>

          <Section title="Billing Defaults">
            <div className="grid grid-cols-2 gap-3">
              <div><FLabel>Default CPT / HCPCS codes</FLabel><FInput data-testid="program-cpt-input" value={form.default_cpt} onChange={f("default_cpt")} placeholder="H0015, 90853" /></div>
              <div><FLabel>Unit definition</FLabel><FInput data-testid="program-unit-input" value={form.unit_definition} onChange={f("unit_definition")} placeholder="Per diem / 15 min" /></div>
              <div><FLabel>Payers accepted</FLabel><FInput data-testid="program-payers-input" value={form.payers_accepted} onChange={f("payers_accepted")} placeholder="GA Medicaid, Amerigroup, self-pay" /></div>
              <div><FLabel>Expected length of stay (days)</FLabel><FInput data-testid="program-los-input" type="number" value={form.expected_los} onChange={f("expected_los")} /></div>
            </div>
            <CheckBox testId="program-auth-required" checked={!!form.auth_required} onChange={() => setForm((s) => ({ ...s, auth_required: !s.auth_required }))} label="Prior authorization required for admission" />
          </Section>

          <Section title="Clinical Criteria & Compliance Timers">
            <div><FLabel>Admission criteria</FLabel><FTextarea data-testid="program-admission-criteria" rows={2} value={form.admission_criteria} onChange={f("admission_criteria")} /></div>
            <div><FLabel>Discharge criteria</FLabel><FTextarea data-testid="program-discharge-criteria" rows={2} value={form.discharge_criteria} onChange={f("discharge_criteria")} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><FLabel>Assessment frequency</FLabel><FInput data-testid="program-assess-freq" value={form.assessment_frequency} onChange={f("assessment_frequency")} placeholder="Every 6 months" /></div>
              <div><FLabel>Treatment plan review (days)</FLabel><FInput data-testid="program-review-days" type="number" value={form.plan_review_days} onChange={f("plan_review_days")} /></div>
            </div>
          </Section>

          <Section title="Custom Fields - anything else your clinic tracks">
            <CustomFieldsBuilder fields={form.custom_fields} onChange={(cf) => setForm((s) => ({ ...s, custom_fields: cf }))} />
          </Section>

          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
            <Btn type="button" variant="ghost" onClick={() => setPanel(false)}>Cancel</Btn>
            <Btn type="submit" disabled={busy} data-testid="save-program-btn">{busy ? "Saving…" : editId ? "Save Changes" : "Add Program"}</Btn>
          </div>
        </form>
      </SlidePanel>

      <ConfirmDialog open={!!confirm} onClose={() => setConfirm(null)} onConfirm={archive}
        description={`Archive program "${confirm?.name}"? Clients currently assigned keep their history.`} />
    </div>
  );
}
